<template>
  <div class="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
    <!-- Header & Action Row -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-gray-200"
    >
      <div>
        <div class="flex items-center space-x-3">
          <h1 class="text-3xl font-bold text-gray-900 font-headline">
            Client Cards
          </h1>
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-tiara-100 text-tiara-800"
          >
            CS Dashboard
          </span>
        </div>
        <p class="text-sm text-gray-500 mt-1">
          Customer Success portfolio overview, client metadata, and engagement
          dashboard.
        </p>
      </div>

      <div class="flex items-center space-x-3">
        <router-link
          to="/admin/organizations"
          class="inline-flex items-center px-3.5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition"
        >
          <svg
            class="w-4 h-4 mr-1.5 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
          Organizations Table
        </router-link>
        <button
          type="button"
          class="inline-flex items-center px-3.5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition"
          @click="openApiKeysModal"
        >
          <svg
            class="w-4 h-4 mr-1.5 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
          API & Claude Keys
        </button>
        <button
          type="button"
          class="inline-flex items-center px-3.5 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-tiara-600 hover:bg-tiara-700 shadow-sm transition"
          :disabled="isLoading"
          @click="fetchClientCards"
        >
          <svg
            class="w-4 h-4 mr-1.5"
            :class="{ 'animate-spin': isLoading }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>
    </div>

    <!-- Highlighted Deep-Link Banner -->
    <div
      v-if="highlightedOrgId && highlightedCard"
      class="bg-tiara-50 border border-tiara-200 rounded-lg p-3.5 flex items-center justify-between"
    >
      <div class="flex items-center space-x-2 text-tiara-900 text-sm">
        <svg
          class="w-5 h-5 text-tiara-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>
          Showing targeted organization:
          <strong>{{
            highlightedCard ? highlightedCard.name || "Unnamed Client" : ""
          }}</strong>
        </span>
      </div>
      <button
        type="button"
        class="text-xs text-tiara-700 hover:text-tiara-900 font-semibold underline"
        @click="clearHighlight"
      >
        Clear filter
      </button>
    </div>

    <!-- Summary Metrics Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Active Clients
        </div>
        <div class="mt-1 text-2xl font-bold text-gray-900">
          {{ summaryStats.activeClients }}
        </div>
        <div class="mt-1 text-xs text-emerald-600 flex items-center">
          <span
            class="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5"
          ></span>
          Paid & active
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Active Projects
        </div>
        <div class="mt-1 text-2xl font-bold text-indigo-600">
          {{ summaryStats.totalActiveProjects }}
        </div>
        <div class="mt-1 text-xs text-gray-500">Across all spaces</div>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Total Spaces
        </div>
        <div class="mt-1 text-2xl font-bold text-blue-600">
          {{ summaryStats.totalSpaces }}
        </div>
        <div class="mt-1 text-xs text-gray-500">Collaboration spaces</div>
      </div>

      <div
        class="bg-white border rounded-xl p-4 shadow-sm transition"
        :class="
          summaryStats.expiringSoonCount > 0
            ? 'border-amber-300 bg-amber-50/40'
            : 'border-gray-200'
        "
      >
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Expiring Soon
        </div>
        <div
          class="mt-1 text-2xl font-bold"
          :class="
            summaryStats.expiringSoonCount > 0
              ? 'text-amber-600'
              : 'text-gray-900'
          "
        >
          {{ summaryStats.expiringSoonCount }}
        </div>
        <div class="mt-1 text-xs text-gray-500">&le; 60 days</div>
      </div>

      <div
        class="bg-white border rounded-xl p-4 shadow-sm transition"
        :class="
          summaryStats.atRiskCount > 0
            ? 'border-red-300 bg-red-50/40'
            : 'border-gray-200'
        "
      >
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          At Risk
        </div>
        <div
          class="mt-1 text-2xl font-bold"
          :class="
            summaryStats.atRiskCount > 0 ? 'text-red-600' : 'text-gray-900'
          "
        >
          {{ summaryStats.atRiskCount }}
        </div>
        <div class="mt-1 text-xs text-gray-500">Needs attention</div>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Demos & Unpaid
        </div>
        <div class="mt-1 text-2xl font-bold text-gray-400">
          {{ summaryStats.junkClients }}
        </div>
        <div class="mt-1 text-xs text-gray-400">Excluded by default</div>
      </div>
    </div>

    <!-- Search, Filters, and Control Bar -->
    <div
      class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-4"
    >
      <div
        class="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
      >
        <!-- Search Input -->
        <div class="relative flex-1 max-w-lg">
          <div
            class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
          >
            <svg
              class="h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Search by client name, CSM, tier, or notes..."
            class="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-tiara-500 focus:border-tiara-500"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            @click="searchTerm = ''"
          >
            &times;
          </button>
        </div>

        <!-- Controls & View Mode -->
        <div class="flex flex-wrap items-center gap-3">
          <!-- Quick Junk Toggle -->
          <label
            class="inline-flex items-center cursor-pointer text-sm font-medium text-gray-700 select-none"
          >
            <input
              v-model="hideJunkAndDemos"
              type="checkbox"
              class="w-4 h-4 text-tiara-600 border-gray-300 rounded focus:ring-tiara-500"
            />
            <span class="ml-2">Hide Demos & Unpaid</span>
          </label>

          <span class="text-gray-300">|</span>

          <!-- CSM Filter Dropdown -->
          <div class="flex items-center space-x-1.5 text-sm text-gray-600">
            <label
              for="csmFilter"
              class="text-xs font-medium uppercase text-gray-500"
              >CSM:</label
            >
            <select
              id="csmFilter"
              v-model="selectedCsmFilter"
              class="py-1.5 pl-2.5 pr-7 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tiara-500"
            >
              <option value="all">All CSMs</option>
              <option v-if="currentPersonHashedId" value="my">
                My Accounts
              </option>
              <option value="unassigned">Unassigned</option>
              <option
                v-for="csm in csmsList"
                :key="csm.hashedId"
                :value="csm.hashedId"
              >
                {{ csm.name }}
              </option>
            </select>
          </div>

          <span class="text-gray-300">|</span>

          <!-- Sort Dropdown -->
          <div class="flex items-center space-x-1.5 text-sm text-gray-600">
            <label
              for="sortBy"
              class="text-xs font-medium uppercase text-gray-500"
              >Sort:</label
            >
            <select
              id="sortBy"
              v-model="sortBy"
              class="py-1.5 pl-2.5 pr-7 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tiara-500"
            >
              <option value="nameAsc">Name (A &rarr; Z)</option>
              <option value="nameDesc">Name (Z &rarr; A)</option>
              <option value="expiringSoon">Contract Ending Soonest</option>
              <option value="activeProjects">Most Active Projects</option>
              <option value="spaces">Most Spaces</option>
              <option value="members">Most Members</option>
            </select>
          </div>

          <!-- View Mode Toggle -->
          <div
            class="flex items-center border border-gray-200 rounded-lg p-0.5 bg-gray-50"
          >
            <button
              type="button"
              class="p-1.5 rounded-md text-sm"
              :class="
                viewMode === 'grid'
                  ? 'bg-white shadow-sm text-tiara-700 font-semibold'
                  : 'text-gray-500 hover:text-gray-700'
              "
              title="Card Grid View"
              @click="viewMode = 'grid'"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
            <button
              type="button"
              class="p-1.5 rounded-md text-sm"
              :class="
                viewMode === 'list'
                  ? 'bg-white shadow-sm text-tiara-700 font-semibold'
                  : 'text-gray-500 hover:text-gray-700'
              "
              title="Compact List View"
              @click="viewMode = 'list'"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Filter Tabs -->
      <div
        class="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100"
      >
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'my'
              ? 'bg-tiara-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'my'"
        >
          My Accounts ({{ myAccountsCount }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'active'
              ? 'bg-tiara-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'active'"
        >
          Active Clients ({{ summaryStats.activeClients }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'all'
              ? 'bg-tiara-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'all'"
        >
          All Clients ({{ summaryStats.totalClients }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'expiring'
              ? 'bg-amber-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'expiring'"
        >
          Expiring Soon ({{ summaryStats.expiringSoonCount }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'atRisk'
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'atRisk'"
        >
          At Risk ({{ summaryStats.atRiskCount }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition"
          :class="
            activeFilterTab === 'junk'
              ? 'bg-gray-700 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="activeFilterTab = 'junk'"
        >
          Demos & Unpaid ({{ summaryStats.junkClients }})
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading && cards.length === 0" class="py-16 text-center">
      <div
        class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-tiara-500 border-t-transparent"
      ></div>
      <p class="mt-4 text-sm text-gray-500">Loading client cards...</p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredCards.length === 0"
      class="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
      <h3 class="mt-3 text-base font-medium text-gray-900">
        No client cards found
      </h3>
      <p class="mt-1 text-sm text-gray-500">
        Try adjusting your search filters, or turn off "Hide Demos & Unpaid".
      </p>
      <div class="mt-4">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
          @click="resetFilters"
        >
          Reset All Filters
        </button>
      </div>
    </div>

    <!-- Grid View Mode -->
    <div
      v-else-if="viewMode === 'grid'"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
    >
      <div
        v-for="card in filteredCards"
        :id="`card-${card.hashedId}`"
        :key="card.hashedId"
        class="bg-white border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between"
        :class="[
          highlightedOrgId === card.hashedId
            ? 'ring-4 ring-tiara-400 border-tiara-500'
            : 'border-gray-200 hover:border-gray-300',
        ]"
      >
        <div class="p-5 space-y-4">
          <!-- Card Header: Logo, Name, Badges -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center space-x-3 min-w-0">
              <!-- Logo or Monogram -->
              <div
                v-if="card.logoHash"
                class="w-12 h-12 rounded-lg border border-gray-100 bg-white shrink-0 bg-contain bg-center bg-no-repeat p-1"
                :style="`background-image: url(${getOrgLogoUrl(card)});`"
              ></div>
              <div
                v-else
                class="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center text-lg font-bold text-white shadow-inner"
                :style="`background-color: ${getAvatarColor(card.name)};`"
              >
                {{ getAvatarInitial(card.name) }}
              </div>

              <!-- Name & Identifier -->
              <div class="min-w-0 flex-1">
                <h3
                  class="text-base font-bold text-gray-900 truncate"
                  :title="card.name || 'Unnamed Client'"
                >
                  {{ card.name || "Unnamed Client" }}
                </h3>
                <div class="flex items-center space-x-1.5 mt-0.5">
                  <span class="text-xs text-mono text-gray-400 font-mono"
                    >cr-{{ card.hashedId }}</span
                  >
                  <span
                    v-if="card.isTestAccount"
                    class="px-1.5 py-0.2 bg-orange-100 text-orange-800 rounded text-[10px] font-semibold"
                  >
                    Test
                  </span>
                  <span
                    v-if="card.clientMetadata.isJunk"
                    class="px-1.5 py-0.2 bg-gray-200 text-gray-700 rounded text-[10px] font-semibold"
                  >
                    Demo/Unpaid
                  </span>
                </div>
              </div>
            </div>

            <!-- Edit Button Top Right -->
            <button
              type="button"
              class="p-1.5 text-gray-400 hover:text-tiara-700 hover:bg-tiara-50 rounded-lg transition"
              title="Edit Client Metadata"
              @click="openEditModal(card)"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          </div>

          <!-- Badges Row: Tier & Status -->
          <div class="flex flex-wrap items-center gap-1.5">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
              :class="getTierBadgeClass(card.clientMetadata.tier)"
            >
              {{ card.clientMetadata.tier || "Standard" }}
            </span>

            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              :class="getStatusBadgeClass(card.clientMetadata.status)"
            >
              <span
                class="w-1.5 h-1.5 rounded-full mr-1.5"
                :class="getStatusDotClass(card.clientMetadata.status)"
              ></span>
              {{ card.clientMetadata.status || "Active" }}
            </span>

            <span
              v-if="card.clientMetadata.contractValue"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
            >
              {{ card.clientMetadata.contractValue }}
            </span>
          </div>

          <!-- Key Metrics Counter Row -->
          <div
            class="grid grid-cols-3 gap-2 bg-gray-50 border border-gray-100 rounded-lg p-3 text-center"
          >
            <!-- Active Projects -->
            <div
              class="cursor-pointer hover:bg-gray-100 p-1 rounded transition"
              @click="toggleExpanded(card.hashedId)"
            >
              <div
                class="text-xs font-medium text-gray-500 flex items-center justify-center"
              >
                <svg
                  class="w-3.5 h-3.5 mr-1 text-indigo-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Projects
              </div>
              <div class="mt-0.5 text-lg font-bold text-indigo-700">
                {{ card.activeProjectsCount }}
              </div>
            </div>

            <!-- Collaboration Spaces -->
            <div
              class="cursor-pointer hover:bg-gray-100 p-1 rounded transition"
              @click="toggleExpanded(card.hashedId)"
            >
              <div
                class="text-xs font-medium text-gray-500 flex items-center justify-center"
              >
                <svg
                  class="w-3.5 h-3.5 mr-1 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                Spaces
              </div>
              <div class="mt-0.5 text-lg font-bold text-blue-700">
                {{ card.collaborationSpacesCount }}
              </div>
            </div>

            <!-- Organization Members -->
            <div class="p-1">
              <div
                class="text-xs font-medium text-gray-500 flex items-center justify-center"
              >
                <svg
                  class="w-3.5 h-3.5 mr-1 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                Members
              </div>
              <div class="mt-0.5 text-lg font-bold text-gray-800">
                {{ card.organizationMembersCount }}
              </div>
            </div>
          </div>

          <!-- Metadata Section (Dates, CSM, Expiration Alert) -->
          <div class="text-xs space-y-2 pt-1 border-t border-gray-100">
            <!-- Contract Term & Expiration -->
            <div class="flex items-center justify-between text-gray-600">
              <span class="text-gray-500 font-medium">Contract Period:</span>
              <span
                v-if="
                  card.clientMetadata.contractStartDate ||
                  card.clientMetadata.contractEndDate
                "
              >
                {{ formatDate(card.clientMetadata.contractStartDate) }} &rarr;
                {{ formatDate(card.clientMetadata.contractEndDate) }}
              </span>
              <span v-else class="text-gray-400 italic">No dates set</span>
            </div>

            <!-- Expiration Countdown / Tag -->
            <div
              v-if="card.clientMetadata.contractEndDate"
              class="flex items-center justify-between"
            >
              <span class="text-gray-500 font-medium">Renewal Status:</span>
              <span
                class="font-semibold px-2 py-0.5 rounded text-[11px]"
                :class="getExpirationClass(card.clientMetadata)"
              >
                {{ getExpirationText(card.clientMetadata) }}
              </span>
            </div>

            <!-- CSM / Account Owner -->
            <div class="flex items-center justify-between text-gray-600">
              <span class="text-gray-500 font-medium">CSM / Owner:</span>
              <div class="flex items-center space-x-1">
                <span
                  class="font-medium"
                  :class="
                    card.clientMetadata.csmName
                      ? 'text-gray-800'
                      : 'text-gray-400 italic'
                  "
                >
                  {{ card.clientMetadata.csmName || "Unassigned" }}
                </span>
                <span
                  v-if="isMyAccount(card)"
                  class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-tiara-100 text-tiara-800 border border-tiara-200"
                >
                  You
                </span>
              </div>
            </div>

            <!-- Next Touchpoint Section -->
            <div class="mt-2.5 pt-2 border-t border-gray-100">
              <div class="flex items-center justify-between mb-1">
                <span
                  class="text-gray-500 font-medium flex items-center gap-1 text-[11px]"
                >
                  <svg
                    class="w-3.5 h-3.5 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Next Touchpoint:
                </span>
                <button
                  type="button"
                  class="text-[11px] font-semibold text-tiara-600 hover:text-tiara-800"
                  @click="openTouchpointsModal(card)"
                >
                  {{
                    card.touchpointsCount > 0
                      ? `${card.touchpointsCount} logged`
                      : "+ Log Meeting"
                  }}
                </button>
              </div>

              <!-- Upcoming Item Display -->
              <div
                v-if="card.nextTouchpoint"
                class="bg-blue-50/80 border border-blue-100 rounded-lg p-2 flex items-start justify-between gap-2 cursor-pointer hover:bg-blue-100/70 transition"
                @click="openTouchpointsModal(card)"
              >
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span
                      class="font-semibold text-gray-800 truncate text-xs"
                      >{{ card.nextTouchpoint.title }}</span
                    >
                    <span
                      class="px-1.5 py-0.2 rounded text-[10px] font-bold"
                      :class="getTouchpointBadgeClass(card.nextTouchpoint)"
                    >
                      {{
                        formatTouchpointDate(card.nextTouchpoint.scheduledAt)
                      }}
                    </span>
                    <span
                      v-if="card.nextTouchpoint.source === 'CLAUDE'"
                      class="px-1 py-0.2 bg-purple-100 text-purple-700 rounded text-[9px] font-semibold"
                    >
                      Claude
                    </span>
                  </div>
                  <div
                    v-if="card.nextTouchpoint.location"
                    class="text-[11px] text-gray-500 truncate mt-0.5"
                  >
                    {{ card.nextTouchpoint.location }}
                  </div>
                </div>
              </div>
              <div
                v-else
                class="text-gray-400 italic text-[11px] flex items-center justify-between py-1"
              >
                <span>No upcoming meetings</span>
                <button
                  type="button"
                  class="text-[11px] text-tiara-600 hover:text-tiara-800 font-medium not-italic"
                  @click="openAddTouchpointModal(card)"
                >
                  + Add
                </button>
              </div>
            </div>

            <!-- CS Notes Preview -->
            <div
              v-if="card.clientMetadata.notes"
              class="mt-2 bg-gray-50 rounded p-2 text-gray-600 border border-gray-100"
            >
              <div
                class="font-semibold text-[11px] text-gray-500 uppercase tracking-wider mb-0.5"
              >
                CS Notes:
              </div>
              <p class="text-xs line-clamp-2 text-gray-700 italic">
                "{{ card.clientMetadata.notes }}"
              </p>
            </div>
          </div>
        </div>

        <!-- Card Footer & Quick Links -->
        <div
          class="px-5 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl flex items-center justify-between text-xs"
        >
          <button
            type="button"
            class="text-tiara-700 hover:text-tiara-900 font-medium flex items-center"
            @click="toggleExpanded(card.hashedId)"
          >
            <span>{{
              isExpanded(card.hashedId)
                ? "Hide details"
                : "View spaces & projects"
            }}</span>
            <svg
              class="w-3.5 h-3.5 ml-1 transition-transform duration-200"
              :class="{ 'rotate-180': isExpanded(card.hashedId) }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <div class="flex items-center space-x-2">
            <!-- Touchpoints Quick Button -->
            <button
              type="button"
              class="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded font-medium flex items-center gap-1 transition"
              title="View Touchpoints & Meetings"
              @click="openTouchpointsModal(card)"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{{ card.touchpointsCount || 0 }}</span>
            </button>

            <!-- 1-Click Toggle Demo/Junk -->
            <button
              type="button"
              class="p-1 text-gray-400 hover:text-gray-600 rounded"
              :title="
                card.clientMetadata.isJunk
                  ? 'Mark as Active Client'
                  : 'Mark as Demo/Unpaid'
              "
              @click="toggleJunkQuick(card)"
            >
              <svg
                v-if="card.clientMetadata.isJunk"
                class="w-4 h-4 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <svg
                v-else
                class="w-4 h-4 text-gray-400 hover:text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </button>

            <!-- Edit Button -->
            <button
              type="button"
              class="text-tiara-600 hover:text-tiara-800 font-semibold"
              @click="openEditModal(card)"
            >
              Edit
            </button>

            <!-- Link to Admin Table -->
            <router-link
              :to="`/admin/organizations`"
              class="text-gray-400 hover:text-gray-600"
              title="Open in Admin Organizations"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </router-link>
          </div>
        </div>

        <!-- Collapsible Details: Spaces & Projects -->
        <div
          v-if="isExpanded(card.hashedId)"
          class="border-t border-gray-200 bg-gray-50/70 p-4 space-y-3 rounded-b-xl"
        >
          <!-- Spaces List -->
          <div>
            <div
              class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center justify-between"
            >
              <span>Collaboration Spaces ({{ card.spaces.length }})</span>
            </div>
            <div
              v-if="card.spaces.length === 0"
              class="text-xs text-gray-400 italic"
            >
              No collaboration spaces yet.
            </div>
            <div v-else class="space-y-1.5 max-h-36 overflow-y-auto">
              <div
                v-for="space in card.spaces"
                :key="space.hashedId"
                class="bg-white border border-gray-200 rounded p-2 flex items-center justify-between text-xs"
              >
                <div class="min-w-0 pr-2">
                  <router-link
                    :to="`/admin/collaboration-spaces/edit/${space.hashedId}`"
                    class="font-medium text-tiara-700 hover:underline truncate block"
                  >
                    {{ space.name }}
                  </router-link>
                  <span class="text-[10px] text-gray-400 font-mono"
                    >cr-{{ space.hashedId }}</span
                  >
                </div>
                <div class="flex items-center space-x-2 shrink-0">
                  <span
                    v-if="space.activeProjectsCount > 0"
                    class="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold text-[10px]"
                  >
                    {{ space.activeProjectsCount }} active
                  </span>
                  <span
                    class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                  >
                    {{ space.calc_ActivityLevel }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent / Active Projects List -->
          <div v-if="card.recentProjects && card.recentProjects.length > 0">
            <div
              class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5"
            >
              Active Projects ({{ card.recentProjects.length }})
            </div>
            <div class="space-y-1 max-h-32 overflow-y-auto">
              <div
                v-for="proj in card.recentProjects"
                :key="proj.hashedId"
                class="bg-white border border-gray-100 rounded px-2 py-1.5 text-xs flex items-center justify-between"
              >
                <div class="truncate pr-2">
                  <span class="font-medium text-gray-800">{{
                    proj.title
                  }}</span>
                  <span class="text-[10px] text-gray-400 block">{{
                    proj.collaborationSpaceName
                  }}</span>
                </div>
                <span
                  class="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-700"
                >
                  {{ proj.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Key Members -->
          <div v-if="card.members && card.members.length > 0">
            <div
              class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center justify-between"
            >
              <span
                >Organization Members ({{
                  card.organizationMembersCount
                }})</span
              >
            </div>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="member in card.members"
                :key="member.hashedId"
                class="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-white border border-gray-200 text-gray-700"
                :title="member.email"
              >
                {{ member.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- List View Mode -->
    <div
      v-else-if="viewMode === 'list'"
      class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead
            class="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider"
          >
            <tr>
              <th scope="col" class="px-5 py-3">Client</th>
              <th scope="col" class="px-3 py-3">Tier & Status</th>
              <th scope="col" class="px-3 py-3 text-center">Projects</th>
              <th scope="col" class="px-3 py-3 text-center">Spaces</th>
              <th scope="col" class="px-3 py-3 text-center">Members</th>
              <th scope="col" class="px-4 py-3">Contract End</th>
              <th scope="col" class="px-4 py-3">CSM</th>
              <th scope="col" class="px-4 py-3">Next Touchpoint</th>
              <th scope="col" class="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="card in filteredCards"
              :id="`row-${card.hashedId}`"
              :key="card.hashedId"
              class="hover:bg-gray-50 transition"
              :class="{ 'bg-tiara-50/50': highlightedOrgId === card.hashedId }"
            >
              <td class="px-5 py-3.5 whitespace-nowrap">
                <div class="flex items-center space-x-3">
                  <div
                    v-if="card.logoHash"
                    class="w-8 h-8 rounded border border-gray-100 bg-white shrink-0 bg-contain bg-center bg-no-repeat"
                    :style="`background-image: url(${getOrgLogoUrl(card)});`"
                  ></div>
                  <div
                    v-else
                    class="w-8 h-8 rounded shrink-0 flex items-center justify-center text-xs font-bold text-white"
                    :style="`background-color: ${getAvatarColor(card.name)};`"
                  >
                    {{ getAvatarInitial(card.name) }}
                  </div>
                  <div>
                    <div
                      class="font-bold text-gray-900 truncate max-w-xs"
                      :title="card.name || 'Unnamed Client'"
                    >
                      {{ card.name || "Unnamed Client" }}
                    </div>
                    <div class="text-xs text-gray-400 font-mono">
                      cr-{{ card.hashedId }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-3.5 whitespace-nowrap">
                <div class="flex items-center space-x-1.5">
                  <span
                    class="px-2 py-0.5 rounded text-xs font-semibold"
                    :class="getTierBadgeClass(card.clientMetadata.tier)"
                  >
                    {{ card.clientMetadata.tier || "Standard" }}
                  </span>
                  <span
                    class="px-2 py-0.5 rounded text-xs font-medium"
                    :class="getStatusBadgeClass(card.clientMetadata.status)"
                  >
                    {{ card.clientMetadata.status || "Active" }}
                  </span>
                </div>
              </td>
              <td
                class="px-3 py-3.5 whitespace-nowrap text-center font-bold text-indigo-600"
              >
                {{ card.activeProjectsCount }}
              </td>
              <td
                class="px-3 py-3.5 whitespace-nowrap text-center font-bold text-blue-600"
              >
                {{ card.collaborationSpacesCount }}
              </td>
              <td
                class="px-3 py-3.5 whitespace-nowrap text-center font-bold text-gray-700"
              >
                {{ card.organizationMembersCount }}
              </td>
              <td class="px-4 py-3.5 whitespace-nowrap">
                <div v-if="card.clientMetadata.contractEndDate">
                  <div class="text-xs font-medium text-gray-900">
                    {{ formatDate(card.clientMetadata.contractEndDate) }}
                  </div>
                  <div
                    class="text-[10px] font-semibold"
                    :class="getExpirationClass(card.clientMetadata)"
                  >
                    {{ getExpirationText(card.clientMetadata) }}
                  </div>
                </div>
                <span v-else class="text-xs text-gray-400 italic">Not set</span>
              </td>
              <td class="px-4 py-3.5 whitespace-nowrap text-xs text-gray-700">
                <div class="flex items-center space-x-1">
                  <span
                    :class="
                      card.clientMetadata.csmName
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-400 italic'
                    "
                  >
                    {{ card.clientMetadata.csmName || "—" }}
                  </span>
                  <span
                    v-if="isMyAccount(card)"
                    class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-tiara-100 text-tiara-800 border border-tiara-200"
                  >
                    You
                  </span>
                </div>
              </td>
              <td class="px-4 py-3.5 whitespace-nowrap text-xs">
                <div
                  v-if="card.nextTouchpoint"
                  class="cursor-pointer hover:opacity-80 transition"
                  @click="openTouchpointsModal(card)"
                >
                  <div
                    class="font-semibold text-gray-900 truncate max-w-[180px]"
                  >
                    {{ card.nextTouchpoint.title }}
                  </div>
                  <div class="flex items-center gap-1 mt-0.5">
                    <span
                      class="px-1.5 py-0.2 rounded text-[10px] font-bold"
                      :class="getTouchpointBadgeClass(card.nextTouchpoint)"
                    >
                      {{
                        formatTouchpointDate(card.nextTouchpoint.scheduledAt)
                      }}
                    </span>
                    <span
                      v-if="card.nextTouchpoint.source === 'CLAUDE'"
                      class="px-1 py-0.2 bg-purple-100 text-purple-700 rounded text-[9px] font-semibold"
                    >
                      Claude
                    </span>
                  </div>
                </div>
                <button
                  v-else
                  type="button"
                  class="text-xs text-tiara-600 hover:text-tiara-800 font-medium"
                  @click="openAddTouchpointModal(card)"
                >
                  + Add
                </button>
              </td>
              <td
                class="px-4 py-3.5 whitespace-nowrap text-right text-xs space-x-2"
              >
                <button
                  type="button"
                  class="text-blue-600 hover:text-blue-800 font-semibold"
                  @click="openTouchpointsModal(card)"
                >
                  Touchpoints ({{ card.touchpointsCount || 0 }})
                </button>
                <button
                  type="button"
                  class="text-tiara-600 hover:text-tiara-800 font-semibold"
                  @click="openEditModal(card)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700"
                  @click="toggleJunkQuick(card)"
                >
                  {{ card.clientMetadata.isJunk ? "Unmark" : "Junk" }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Edit Client Metadata Modal -->
    <div
      v-if="isEditModalOpen"
      class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      @click.self="closeEditModal"
    >
      <div
        class="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-200"
      >
        <!-- Modal Header -->
        <div
          class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
        >
          <div>
            <h3 class="text-lg font-bold text-gray-900">
              Edit Client Metadata
            </h3>
            <p class="text-xs text-gray-500 mt-0.5">
              Client:
              <strong class="text-gray-800">{{
                editingCard ? editingCard.name || "Unnamed Client" : ""
              }}</strong>
              (cr-{{ editingCard ? editingCard.hashedId : "" }})
              <span class="text-tiara-700 font-medium ml-1"
                >&bull; Invisible to client</span
              >
            </p>
          </div>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none p-1"
            @click="closeEditModal"
          >
            &times;
          </button>
        </div>

        <!-- Modal Body Form -->
        <form
          class="p-6 space-y-4 max-h-[75vh] overflow-y-auto"
          @submit.prevent="saveMetadata"
        >
          <!-- Contract Dates Row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Contract Start Date
              </label>
              <input
                v-model="editForm.contractStartDate"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tiara-500"
              />
            </div>
            <div>
              <label
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Contract End Date
              </label>
              <input
                v-model="editForm.contractEndDate"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tiara-500"
              />
            </div>
          </div>

          <!-- Tier, Status & Value Row -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Account Tier
              </label>
              <select
                v-model="editForm.tier"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tiara-500"
              >
                <option value="Enterprise">Enterprise</option>
                <option value="Growth">Growth</option>
                <option value="Starter">Starter</option>
                <option value="Pilot">Pilot</option>
                <option value="Standard">Standard</option>
                <option value="Demo">Demo</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </div>

            <div>
              <label
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Health Status
              </label>
              <select
                v-model="editForm.status"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tiara-500"
              >
                <option value="Active">Active</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Healthy">Healthy</option>
                <option value="At Risk">At Risk</option>
                <option value="Churned">Churned</option>
                <option value="Demo">Demo</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </div>

            <div>
              <label
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Contract Value / ARR
              </label>
              <input
                v-model="editForm.contractValue"
                type="text"
                placeholder="e.g. $50,000 / yr"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tiara-500"
              />
            </div>
          </div>

          <!-- CSM & Junk Flag Row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div>
              <label
                for="editCsmSelect"
                class="block text-xs font-semibold text-gray-700 uppercase mb-1"
              >
                Customer Success Manager (CSM)
              </label>
              <select
                id="editCsmSelect"
                v-model="editForm.csmHashedId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tiara-500"
              >
                <option :value="null">-- Unassigned --</option>
                <option
                  v-if="currentPersonHashedId"
                  :value="currentPersonHashedId"
                >
                  {{ currentUser.name || "Me" }} (Assign to me)
                </option>
                <option
                  v-for="csm in csmsList"
                  :key="csm.hashedId"
                  :value="csm.hashedId"
                >
                  {{ csm.name }} ({{ csm.email }})
                </option>
              </select>
            </div>

            <!-- Junk / Demo Flag Toggle -->
            <div class="pt-4">
              <label
                class="inline-flex items-center cursor-pointer p-3 border rounded-lg bg-gray-50 border-gray-200 hover:bg-gray-100 transition w-full"
              >
                <input
                  v-model="editForm.isJunk"
                  type="checkbox"
                  class="w-4 h-4 text-tiara-600 border-gray-300 rounded focus:ring-tiara-500"
                />
                <div class="ml-2.5">
                  <span class="text-xs font-bold text-gray-900 block"
                    >Flag as Demo / Unpaid / Junk</span
                  >
                  <span class="text-[11px] text-gray-500 block"
                    >Excludes this client from active dashboard lists</span
                  >
                </div>
              </label>
            </div>
          </div>

          <!-- Internal CS Notes -->
          <div>
            <label
              class="block text-xs font-semibold text-gray-700 uppercase mb-1"
            >
              Internal CS Notes & Relationship History
            </label>
            <textarea
              v-model="editForm.notes"
              rows="4"
              placeholder="Record notes about contract negotiations, key stakeholders, renewal priorities, pain points, etc..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tiara-500"
            ></textarea>
          </div>

          <!-- Custom Fields ("etc." fields) -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-semibold text-gray-700 uppercase">
                Custom Fields ("Etc." Metadata)
              </label>
              <button
                type="button"
                class="text-xs text-tiara-600 hover:text-tiara-800 font-semibold"
                @click="addCustomField"
              >
                + Add Field
              </button>
            </div>

            <div
              v-if="customFieldsList.length === 0"
              class="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded border border-gray-200"
            >
              No custom fields. Click "+ Add Field" to store custom metadata
              (e.g. Billing Frequency, Slack Channel, etc).
            </div>

            <div v-else class="space-y-2">
              <div
                v-for="(field, index) in customFieldsList"
                :key="index"
                class="flex items-center space-x-2"
              >
                <input
                  v-model="field.key"
                  type="text"
                  placeholder="Key (e.g. Slack Channel)"
                  class="w-1/2 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                />
                <input
                  v-model="field.value"
                  type="text"
                  placeholder="Value"
                  class="w-1/2 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                />
                <button
                  type="button"
                  class="text-gray-400 hover:text-red-500 p-1"
                  @click="removeCustomField(index)"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>

          <!-- Modal Actions Footer -->
          <div
            class="pt-4 border-t border-gray-200 flex items-center justify-end space-x-3"
          >
            <button
              type="button"
              class="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
              @click="closeEditModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-5 py-2 rounded-lg text-sm font-medium text-white bg-tiara-600 hover:bg-tiara-700 shadow-sm transition inline-flex items-center"
              :disabled="isSaving"
            >
              <svg
                v-if="isSaving"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isSaving ? "Saving..." : "Save Metadata" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Organization Touchpoints Modal -->
    <div
      v-if="isTouchpointsModalOpen && activeTouchpointsCard"
      class="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      @click.self="closeTouchpointsModal"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <!-- Modal Header -->
        <div
          class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
        >
          <div class="flex items-center space-x-3">
            <div
              v-if="activeTouchpointsCard.logoHash"
              class="w-10 h-10 rounded-lg border border-gray-200 bg-white shrink-0 bg-contain bg-center bg-no-repeat p-0.5"
              :style="`background-image: url(${getOrgLogoUrl(activeTouchpointsCard)});`"
            ></div>
            <div
              v-else
              class="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center text-base font-bold text-white shadow-inner"
              :style="`background-color: ${getAvatarColor(activeTouchpointsCard.name)};`"
            >
              {{ getAvatarInitial(activeTouchpointsCard.name) }}
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-lg font-bold text-gray-900 font-headline">
                  {{ activeTouchpointsCard.name || "Unnamed Client" }}
                </h3>
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold"
                >
                  Touchpoints & Meetings
                </span>
              </div>
              <p class="text-xs text-gray-500">
                Log and view meetings, check-ins, notes, and Claude-synced
                touchpoints.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-gray-100 transition"
            @click="closeTouchpointsModal"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="px-6 py-5 overflow-y-auto space-y-5 flex-1">
          <!-- Action Bar: Add Touchpoint Button & Refresh -->
          <div class="flex items-center justify-between pb-1">
            <h4 class="text-sm font-bold text-gray-900">
              Meetings & Touchpoints ({{ touchpointsList.length }})
            </h4>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-tiara-600 hover:bg-tiara-700 text-white shadow-sm transition"
                @click="showCreateTouchpointForm = !showCreateTouchpointForm"
              >
                <svg
                  class="w-3.5 h-3.5 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {{ showCreateTouchpointForm ? "Cancel" : "Log Touchpoint" }}
              </button>
              <button
                type="button"
                class="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition"
                title="Refresh Touchpoints"
                @click="fetchTouchpointsForCard(activeTouchpointsCard)"
              >
                <svg
                  class="w-4 h-4"
                  :class="{ 'animate-spin': isLoadingTouchpoints }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Create Touchpoint Form (collapsible) -->
          <div
            v-if="showCreateTouchpointForm"
            class="bg-tiara-50/60 border border-tiara-200 rounded-xl p-4 space-y-3"
          >
            <div
              class="font-bold text-xs text-tiara-900 uppercase tracking-wider"
            >
              Log Touchpoint / Schedule Meeting
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1"
                  >Title *</label
                >
                <input
                  v-model="newTouchpointForm.title"
                  type="text"
                  placeholder="e.g. Q4 Executive Review, Weekly Catchup"
                  class="w-full text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1"
                  >Touchpoint Type</label
                >
                <select
                  v-model="newTouchpointForm.meetingType"
                  class="w-full text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
                >
                  <option value="MEETING">Meeting (Video / In-person)</option>
                  <option value="CALL">Phone Call</option>
                  <option value="EMAIL">Email Follow-up</option>
                  <option value="NOTE">Check-in Note</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1"
                  >Scheduled Date & Time *</label
                >
                <input
                  v-model="newTouchpointForm.scheduledAt"
                  type="datetime-local"
                  class="w-full text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1"
                  >Location / Video Link</label
                >
                <input
                  v-model="newTouchpointForm.location"
                  type="text"
                  placeholder="https://meet.google.com/... or Zoom"
                  class="w-full text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
                />
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1"
                >Notes / Agenda / Outcomes</label
              >
              <textarea
                v-model="newTouchpointForm.notes"
                rows="2"
                placeholder="Discussion topics, next steps, action items..."
                class="w-full text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
              ></textarea>
            </div>

            <div class="flex items-center justify-end space-x-2 pt-1">
              <button
                type="button"
                class="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                @click="showCreateTouchpointForm = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="px-4 py-1.5 text-xs font-semibold text-white bg-tiara-600 hover:bg-tiara-700 rounded-lg shadow-sm transition disabled:opacity-50"
                :disabled="isCreatingTouchpoint || !newTouchpointForm.title"
                @click="submitCreateTouchpoint"
              >
                {{ isCreatingTouchpoint ? "Saving..." : "Save Touchpoint" }}
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div
            v-if="isLoadingTouchpoints"
            class="py-10 text-center text-gray-500 text-xs"
          >
            <div
              class="inline-block animate-spin rounded-full h-6 w-6 border-2 border-tiara-500 border-t-transparent mb-2"
            ></div>
            <div>Loading touchpoints...</div>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="touchpointsList.length === 0"
            class="text-center py-10 bg-gray-50 border border-dashed border-gray-200 rounded-xl"
          >
            <svg
              class="mx-auto h-8 w-8 text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <div class="text-xs font-semibold text-gray-700">
              No touchpoints logged yet
            </div>
            <div class="text-[11px] text-gray-400 mt-0.5">
              Schedule your next meeting or sync upcoming meetings via Claude.
            </div>
            <button
              type="button"
              class="mt-3 text-xs text-tiara-600 hover:text-tiara-800 font-semibold"
              @click="showCreateTouchpointForm = true"
            >
              + Log the first touchpoint
            </button>
          </div>

          <!-- Touchpoints Timeline List -->
          <div v-else class="space-y-3">
            <div
              v-for="tp in touchpointsList"
              :key="tp.hashedId"
              class="bg-white border rounded-xl p-3.5 shadow-sm space-y-2 hover:border-gray-300 transition"
              :class="
                tp.status === 'COMPLETED'
                  ? 'border-gray-200 bg-gray-50/40'
                  : 'border-blue-100'
              "
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-bold text-sm text-gray-900">{{
                      tp.title
                    }}</span>
                    <span
                      class="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                      :class="getTouchpointBadgeClass(tp)"
                    >
                      {{ tp.status }}
                    </span>
                    <span
                      class="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600"
                    >
                      {{ tp.meetingType }}
                    </span>
                    <span
                      v-if="tp.source === 'CLAUDE'"
                      class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 flex items-center gap-1"
                    >
                      <svg
                        class="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      Claude
                    </span>
                  </div>

                  <div
                    class="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap"
                  >
                    <span
                      class="flex items-center gap-1 font-medium text-gray-700"
                    >
                      <svg
                        class="w-3.5 h-3.5 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {{ formatTouchpointDate(tp.scheduledAt) }}
                    </span>
                    <span v-if="tp.creatorName" class="text-gray-400">
                      by {{ tp.creatorName }}
                    </span>
                    <a
                      v-if="tp.location && tp.location.startsWith('http')"
                      :href="tp.location"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      Join Meeting
                    </a>
                    <span v-else-if="tp.location" class="text-gray-500">
                      {{ tp.location }}
                    </span>
                  </div>
                </div>

                <!-- Touchpoint Item Actions -->
                <div class="flex items-center space-x-1 shrink-0">
                  <button
                    v-if="tp.status === 'SCHEDULED'"
                    type="button"
                    class="px-2 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-200 flex items-center gap-1 transition"
                    title="Mark Completed"
                    @click="markTouchpointCompleted(tp)"
                  >
                    <svg
                      class="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>Done</span>
                  </button>
                  <button
                    type="button"
                    class="p-1 text-gray-400 hover:text-red-600 rounded transition"
                    title="Delete Touchpoint"
                    @click="deleteTouchpoint(tp)"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Notes display -->
              <div
                v-if="tp.notes"
                class="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5 border border-gray-100"
              >
                <span
                  class="font-semibold text-gray-500 uppercase tracking-wider text-[10px] block mb-0.5"
                >
                  Notes:
                </span>
                <p class="whitespace-pre-line">{{ tp.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end"
        >
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
            @click="closeTouchpointsModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Admin API Keys & Claude Integration Modal -->
    <div
      v-if="isApiKeysModalOpen"
      class="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      @click.self="closeApiKeysModal"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <!-- Modal Header -->
        <div
          class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
        >
          <div class="flex items-center space-x-3">
            <div
              class="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-gray-900 font-headline">
                Admin API Keys & Claude Integration
              </h3>
              <p class="text-xs text-gray-500">
                Issue personal admin API keys to sync upcoming meetings or
                touchpoints from Claude, MCP, or scripts.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-gray-100 transition"
            @click="closeApiKeysModal"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="px-6 py-5 overflow-y-auto space-y-6 flex-1 text-xs">
          <!-- Newly Generated Key Alert -->
          <div
            v-if="newlyCreatedToken"
            class="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2 animate-in fade-in"
          >
            <div class="flex items-center justify-between">
              <span
                class="font-bold text-emerald-900 flex items-center gap-1.5"
              >
                <svg
                  class="w-4 h-4 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                New API Key Generated!
              </span>
              <span
                class="text-[10px] text-emerald-700 font-semibold bg-emerald-100 px-2 py-0.5 rounded"
              >
                Copy now — will not be shown again
              </span>
            </div>
            <div class="flex items-center gap-2">
              <code
                class="flex-1 bg-white border border-emerald-300 rounded px-2.5 py-1.5 font-mono text-xs text-emerald-950 select-all overflow-x-auto"
              >
                {{ newlyCreatedToken }}
              </code>
              <button
                type="button"
                class="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shrink-0 flex items-center gap-1"
                @click="copyText(newlyCreatedToken, 'token')"
              >
                <svg
                  class="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>{{ copiedToken ? "Copied!" : "Copy Key" }}</span>
              </button>
            </div>
          </div>

          <!-- Create Key Section -->
          <div
            class="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3"
          >
            <h4 class="font-bold text-gray-900">
              Generate a New Admin API Key
            </h4>
            <div class="flex items-center gap-2">
              <input
                v-model="newApiKeyName"
                type="text"
                placeholder="Key label, e.g. 'Claude Calendar Sync'"
                class="flex-1 text-xs rounded-lg border-gray-300 shadow-sm focus:border-tiara-500 focus:ring-tiara-500"
                @keyup.enter="generateApiKey"
              />
              <button
                type="button"
                class="px-4 py-2 rounded-lg text-xs font-semibold bg-tiara-600 hover:bg-tiara-700 text-white shadow-sm shrink-0 disabled:opacity-50"
                :disabled="isCreatingApiKey || !newApiKeyName.trim()"
                @click="generateApiKey"
              >
                {{ isCreatingApiKey ? "Generating..." : "Generate Key" }}
              </button>
            </div>
          </div>

          <!-- Existing Keys List -->
          <div class="space-y-3">
            <h4
              class="font-bold text-gray-900 flex items-center justify-between"
            >
              <span>Your Active API Keys</span>
              <span class="text-gray-400 font-normal"
                >({{ apiKeysList.length }})</span
              >
            </h4>

            <div v-if="isLoadingApiKeys" class="py-6 text-center text-gray-400">
              Loading keys...
            </div>

            <div
              v-else-if="apiKeysList.length === 0"
              class="text-center py-6 bg-gray-50 border border-dashed rounded-xl text-gray-500"
            >
              No API keys generated yet.
            </div>

            <div v-else class="space-y-2">
              <div
                v-for="key in apiKeysList"
                :key="key.hashedId"
                class="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3 shadow-sm hover:border-gray-300"
              >
                <div class="min-w-0 flex-1">
                  <div class="font-semibold text-gray-900 truncate">
                    {{ key.name || "Unnamed Key" }}
                  </div>
                  <div
                    class="flex items-center gap-2 text-[11px] text-gray-400 mt-0.5"
                  >
                    <span class="font-mono">{{
                      key.token || "ns_••••••••••••••••"
                    }}</span>
                    <span>&bull;</span>
                    <span>Created {{ formatDate(key.createdAt) }}</span>
                  </div>
                </div>

                <div class="flex items-center space-x-1.5 shrink-0">
                  <button
                    v-if="key.token"
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="Copy Key"
                    @click="copyText(key.token, 'key')"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Revoke Key"
                    @click="deleteApiKey(key)"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Claude Integration Instructions -->
          <div
            class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2"
          >
            <h4 class="font-bold text-slate-900 flex items-center gap-1.5">
              <svg
                class="w-4 h-4 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              How to Connect with Claude / Calendar Sync
            </h4>
            <p class="text-slate-600 text-[11px] leading-relaxed">
              Authenticate requests using your API key in the
              <code class="bg-white px-1 py-0.5 rounded border font-mono"
                >X-API-KEY: ns_...</code
              >
              or
              <code class="bg-white px-1 py-0.5 rounded border font-mono"
                >Authorization: Bearer ns_...</code
              >
              header.
            </p>

            <div class="relative">
              <pre
                class="bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto text-[11px] font-mono leading-relaxed"
              >
curl -X POST "https://api.notedsource.com/api/v0/organization/&lt;ORG_ID&gt;/touchpoints/" \
  -H "X-API-KEY: ns_..." \
  -H "Content-Type: application/json" \
  -d '{
    "title": "Quarterly Business Review",
    "meeting_type": "MEETING",
    "scheduled_at": "2026-10-15T14:00:00Z",
    "location": "https://meet.google.com/abc-defg",
    "notes": "Discuss renewal & team expansion",
    "source": "CLAUDE",
    "external_id": "google-calendar-event-id-123"
  }'</pre
              >
              <button
                type="button"
                class="absolute top-2 right-2 px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-[10px] font-semibold border border-slate-700"
                @click="copyCurlSnippet"
              >
                {{ copiedCurl ? "Copied!" : "Copy Curl" }}
              </button>
            </div>
            <p class="text-[11px] text-slate-500">
              * The <code class="font-mono">external_id</code> field provides
              idempotency: if Claude calls this multiple times with the same
              calendar event ID, it will update the existing touchpoint instead
              of creating duplicates.
            </p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end"
        >
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
            @click="closeApiKeysModal"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { utils } from "common-frontend";

const { keysToCamel } = utils;

export default {
  name: "AdminClientCards",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      cards: [],
      summaryStats: {
        totalClients: 0,
        activeClients: 0,
        junkClients: 0,
        totalActiveProjects: 0,
        totalSpaces: 0,
        expiringSoonCount: 0,
        atRiskCount: 0,
      },
      searchTerm: "",
      hideJunkAndDemos: true,
      activeFilterTab: "active", // 'my', 'active', 'all', 'expiring', 'atRisk', 'junk'
      selectedCsmFilter: "all", // 'all', 'my', 'unassigned', or csm hashedId
      csmsList: [],
      sortBy: "nameAsc", // 'nameAsc', 'nameDesc', 'expiringSoon', 'activeProjects', 'spaces', 'members'
      viewMode: "grid", // 'grid' | 'list'
      expandedOrgIds: {},
      highlightedOrgId: null,

      // Modal state
      isEditModalOpen: false,
      editingCard: null,
      editForm: {
        contractStartDate: "",
        contractEndDate: "",
        contractValue: "",
        tier: "Standard",
        status: "Active",
        isJunk: false,
        csmHashedId: null,
        csmName: "",
        notes: "",
      },
      customFieldsList: [],

      // Touchpoints modal state
      isTouchpointsModalOpen: false,
      activeTouchpointsCard: null,
      touchpointsList: [],
      isLoadingTouchpoints: false,
      isCreatingTouchpoint: false,
      showCreateTouchpointForm: false,
      newTouchpointForm: {
        title: "",
        meetingType: "MEETING",
        scheduledAt: "",
        location: "",
        notes: "",
        source: "MANUAL",
      },

      // API Keys modal state
      isApiKeysModalOpen: false,
      isLoadingApiKeys: false,
      isCreatingApiKey: false,
      apiKeysList: [],
      newApiKeyName: "",
      newlyCreatedToken: null,
      copiedToken: false,
      copiedCurl: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.person || this.$store?.getters?.person || {};
    },
    currentPersonHashedId() {
      return this.currentUser?.hashedId || null;
    },
    myAccountsCount() {
      if (!this.cards || !this.cards.length) return 0;
      return this.cards.filter(
        (c) =>
          this.isMyAccount(c) && !c.clientMetadata.isJunk && !c.isTestAccount,
      ).length;
    },
    highlightedCard() {
      if (!this.highlightedOrgId) return null;
      return (
        this.cards.find((c) => c.hashedId === this.highlightedOrgId) || null
      );
    },
    filteredCards() {
      let result = [...this.cards];

      // Tab filter
      if (this.activeFilterTab === "my") {
        result = result.filter(
          (c) =>
            this.isMyAccount(c) && !c.clientMetadata.isJunk && !c.isTestAccount,
        );
      } else if (this.activeFilterTab === "active") {
        result = result.filter(
          (c) =>
            !c.clientMetadata.isJunk &&
            !c.isTestAccount &&
            c.clientMetadata.tier !== "Demo" &&
            c.clientMetadata.tier !== "Unpaid" &&
            c.clientMetadata.status !== "Churned" &&
            c.clientMetadata.status !== "Demo" &&
            c.clientMetadata.status !== "Unpaid",
        );
      } else if (this.activeFilterTab === "expiring") {
        result = result.filter((c) => c.clientMetadata.isExpiringSoon);
      } else if (this.activeFilterTab === "atRisk") {
        result = result.filter((c) => c.clientMetadata.status === "At Risk");
      } else if (this.activeFilterTab === "junk") {
        result = result.filter(
          (c) =>
            c.clientMetadata.isJunk ||
            c.isTestAccount ||
            c.clientMetadata.tier === "Demo" ||
            c.clientMetadata.tier === "Unpaid" ||
            c.clientMetadata.status === "Demo" ||
            c.clientMetadata.status === "Unpaid",
        );
      }

      // Checkbox filter: Hide junk & demos
      if (this.hideJunkAndDemos && this.activeFilterTab !== "junk") {
        result = result.filter(
          (c) =>
            !c.clientMetadata.isJunk &&
            !c.isTestAccount &&
            c.clientMetadata.tier !== "Demo" &&
            c.clientMetadata.tier !== "Unpaid",
        );
      }

      // CSM selector dropdown filter
      if (this.selectedCsmFilter === "my") {
        result = result.filter((c) => this.isMyAccount(c));
      } else if (this.selectedCsmFilter === "unassigned") {
        result = result.filter(
          (c) => !c.clientMetadata.csmHashedId && !c.clientMetadata.csmName,
        );
      } else if (this.selectedCsmFilter !== "all") {
        result = result.filter(
          (c) => c.clientMetadata.csmHashedId === this.selectedCsmFilter,
        );
      }

      // Search term filter
      if (this.searchTerm && this.searchTerm.trim()) {
        const query = this.searchTerm.toLowerCase().trim();
        result = result.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const csm = (c.clientMetadata.csmName || "").toLowerCase();
          const tier = (c.clientMetadata.tier || "").toLowerCase();
          const notes = (c.clientMetadata.notes || "").toLowerCase();
          const id = (c.hashedId || "").toLowerCase();
          return (
            name.includes(query) ||
            csm.includes(query) ||
            tier.includes(query) ||
            notes.includes(query) ||
            id.includes(query)
          );
        });
      }

      // Sorting
      result.sort((a, b) => {
        if (this.sortBy === "nameAsc") {
          return (a.name || "").localeCompare(b.name || "");
        }
        if (this.sortBy === "nameDesc") {
          return (b.name || "").localeCompare(a.name || "");
        }
        if (this.sortBy === "expiringSoon") {
          const aDays = a.clientMetadata.daysUntilExpiration ?? 99999;
          const bDays = b.clientMetadata.daysUntilExpiration ?? 99999;
          return aDays - bDays;
        }
        if (this.sortBy === "activeProjects") {
          return b.activeProjectsCount - a.activeProjectsCount;
        }
        if (this.sortBy === "spaces") {
          return b.collaborationSpacesCount - a.collaborationSpacesCount;
        }
        if (this.sortBy === "members") {
          return b.organizationMembersCount - a.organizationMembersCount;
        }
        return 0;
      });

      return result;
    },
  },
  watch: {
    "$route.query.org": {
      immediate: true,
      handler(orgId) {
        if (orgId) {
          this.highlightedOrgId = orgId;
          // Switch to all tabs so targeted card is guaranteed visible
          this.hideJunkAndDemos = false;
          this.activeFilterTab = "all";
          this.$nextTick(() => {
            this.scrollToCard(orgId);
          });
        }
      },
    },
  },
  mounted() {
    this.fetchClientCards();
  },
  methods: {
    fetchClientCards() {
      this.isLoading = true;
      this.$api.axios
        .get("/organization/client-cards/")
        .then((resp) => {
          const data = keysToCamel(resp.data);
          this.cards = data.cards || [];
          if (data.csms) {
            this.csmsList = data.csms;
          }
          if (data.summaryStats) {
            this.summaryStats = data.summaryStats;
          }
          if (this.highlightedOrgId) {
            this.$nextTick(() => {
              this.scrollToCard(this.highlightedOrgId);
            });
          }
        })
        .catch(() => {
          this.$toast.error("Failed to load client cards");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    isMyAccount(card) {
      if (!card || !card.clientMetadata) return false;
      if (
        this.currentPersonHashedId &&
        card.clientMetadata.csmHashedId === this.currentPersonHashedId
      ) {
        return true;
      }
      if (
        this.currentUser?.name &&
        card.clientMetadata.csmName &&
        card.clientMetadata.csmName.toLowerCase() ===
          this.currentUser.name.toLowerCase()
      ) {
        return true;
      }
      return false;
    },
    scrollToCard(orgId) {
      const el =
        document.getElementById(`card-${orgId}`) ||
        document.getElementById(`row-${orgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    clearHighlight() {
      this.highlightedOrgId = null;
      this.hideJunkAndDemos = true;
      this.activeFilterTab = "active";
      this.selectedCsmFilter = "all";
      this.$router.replace({ query: {} });
    },
    resetFilters() {
      this.searchTerm = "";
      this.hideJunkAndDemos = false;
      this.activeFilterTab = "all";
      this.selectedCsmFilter = "all";
      this.sortBy = "nameAsc";
    },
    toggleExpanded(hashedId) {
      this.expandedOrgIds = {
        ...this.expandedOrgIds,
        [hashedId]: !this.expandedOrgIds[hashedId],
      };
    },
    isExpanded(hashedId) {
      return !!this.expandedOrgIds[hashedId];
    },
    getOrgLogoUrl(card) {
      if (!card.logoHash) return "";
      const base = import.meta.env.VITE_API_BASE_URL || "";
      return `${base}/file/${card.logoHash}/content/`;
    },
    getAvatarInitial(name) {
      if (!name || !name.trim()) return "?";
      return name.trim().charAt(0).toUpperCase();
    },
    getAvatarColor(name) {
      const colors = [
        "#4F46E5",
        "#0284C7",
        "#059669",
        "#7C3AED",
        "#DB2777",
        "#D97706",
        "#2563EB",
      ];
      let sum = 0;
      for (let i = 0; i < (name || "").length; i += 1) {
        sum += name.charCodeAt(i);
      }
      return colors[sum % colors.length];
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return dayjs(dateStr).format("MMM D, YYYY");
    },
    getExpirationText(meta) {
      if (!meta.contractEndDate) return "";
      const days = meta.daysUntilExpiration;
      if (days === null || days === undefined) return "";
      if (days < 0) return `Expired ${Math.abs(days)}d ago`;
      if (days === 0) return "Expires today";
      if (days <= 60) return `Renews in ${days}d`;
      return `${days}d remaining`;
    },
    getExpirationClass(meta) {
      const days = meta.daysUntilExpiration;
      if (days === null || days === undefined) return "text-gray-500";
      if (days < 0) return "bg-red-100 text-red-800";
      if (days <= 30) return "bg-red-100 text-red-800";
      if (days <= 60) return "bg-amber-100 text-amber-800";
      return "bg-emerald-100 text-emerald-800";
    },
    getTierBadgeClass(tier) {
      const map = {
        Enterprise: "bg-indigo-100 text-indigo-800",
        Growth: "bg-blue-100 text-blue-800",
        Starter: "bg-cyan-100 text-cyan-800",
        Pilot: "bg-purple-100 text-purple-800",
        Standard: "bg-gray-100 text-gray-800",
        Demo: "bg-orange-100 text-orange-800",
        Unpaid: "bg-gray-200 text-gray-600",
      };
      return map[tier] || "bg-gray-100 text-gray-800";
    },
    getStatusBadgeClass(status) {
      const map = {
        Active: "bg-emerald-50 text-emerald-700",
        Healthy: "bg-emerald-50 text-emerald-700",
        Onboarding: "bg-blue-50 text-blue-700",
        "At Risk": "bg-red-50 text-red-700",
        Churned: "bg-gray-100 text-gray-600",
        Demo: "bg-orange-50 text-orange-700",
        Unpaid: "bg-gray-100 text-gray-600",
      };
      return map[status] || "bg-gray-50 text-gray-700";
    },
    getStatusDotClass(status) {
      const map = {
        Active: "bg-emerald-500",
        Healthy: "bg-emerald-500",
        Onboarding: "bg-blue-500",
        "At Risk": "bg-red-500",
        Churned: "bg-gray-400",
        Demo: "bg-orange-500",
        Unpaid: "bg-gray-400",
      };
      return map[status] || "bg-gray-400";
    },
    toggleJunkQuick(card) {
      const newJunkVal = !card.clientMetadata.isJunk;
      let newTier = card.clientMetadata.tier;
      if (newJunkVal) {
        newTier = "Demo";
      } else if (card.clientMetadata.tier === "Demo") {
        newTier = "Standard";
      }
      this.$api.axios
        .put(`/organization/${card.hashedId}/client-metadata/`, {
          is_junk: newJunkVal,
          tier: newTier,
        })
        .then((resp) => {
          const updated = keysToCamel(resp.data);
          card.clientMetadata.isJunk = updated.isJunk;
          card.clientMetadata.tier = updated.tier;
          const clientName = card.name || "Client";
          this.$toast.success(
            newJunkVal
              ? `Marked ${clientName} as Demo/Unpaid`
              : `Marked ${clientName} as Active Client`,
          );
        })
        .catch(() => {
          this.$toast.error("Failed to update status");
        });
    },
    openEditModal(card) {
      this.editingCard = card;
      const meta = card.clientMetadata || {};
      this.editForm = {
        contractStartDate: meta.contractStartDate
          ? meta.contractStartDate.substring(0, 10)
          : "",
        contractEndDate: meta.contractEndDate
          ? meta.contractEndDate.substring(0, 10)
          : "",
        contractValue: meta.contractValue || "",
        tier: meta.tier || "Standard",
        status: meta.status || "Active",
        isJunk: !!meta.isJunk,
        csmHashedId: meta.csmHashedId || null,
        csmName: meta.csmName || "",
        notes: meta.notes || "",
      };

      // Custom fields
      this.customFieldsList = [];
      if (meta.customFields && typeof meta.customFields === "object") {
        Object.entries(meta.customFields).forEach(([k, v]) => {
          this.customFieldsList.push({ key: k, value: String(v) });
        });
      }

      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.isEditModalOpen = false;
      this.editingCard = null;
    },
    addCustomField() {
      this.customFieldsList.push({ key: "", value: "" });
    },
    removeCustomField(index) {
      this.customFieldsList.splice(index, 1);
    },
    saveMetadata() {
      if (!this.editingCard) return;

      this.isSaving = true;

      // Pack custom fields
      const customFields = {};
      this.customFieldsList.forEach((item) => {
        if (item.key && item.key.trim()) {
          customFields[item.key.trim()] = item.value;
        }
      });

      let selectedCsmName = null;
      if (this.editForm.csmHashedId) {
        const found = this.csmsList.find(
          (c) => c.hashedId === this.editForm.csmHashedId,
        );
        selectedCsmName = found ? found.name : this.editForm.csmName;
      }

      const payload = {
        contract_start_date: this.editForm.contractStartDate || null,
        contract_end_date: this.editForm.contractEndDate || null,
        contract_value: this.editForm.contractValue || null,
        tier: this.editForm.tier || null,
        status: this.editForm.status || null,
        is_junk: this.editForm.isJunk,
        csm_hashed_id: this.editForm.csmHashedId || null,
        csm_name: selectedCsmName || null,
        notes: this.editForm.notes || null,
        custom_fields: customFields,
      };

      this.$api.axios
        .put(
          `/organization/${this.editingCard.hashedId}/client-metadata/`,
          payload,
        )
        .then((resp) => {
          const updated = keysToCamel(resp.data);
          this.editingCard.clientMetadata = {
            ...this.editingCard.clientMetadata,
            ...updated,
          };

          // Recompute days until expiration
          if (updated.contractEndDate) {
            const today = dayjs().startOf("day");
            const end = dayjs(updated.contractEndDate).startOf("day");
            const diff = end.diff(today, "day");
            this.editingCard.clientMetadata.daysUntilExpiration = diff;
            this.editingCard.clientMetadata.isExpiringSoon =
              diff >= 0 && diff <= 60;
          } else {
            this.editingCard.clientMetadata.daysUntilExpiration = null;
            this.editingCard.clientMetadata.isExpiringSoon = false;
          }

          this.$toast.success("Client metadata saved successfully");
          this.closeEditModal();
        })
        .catch(() => {
          this.$toast.error("Failed to save metadata");
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    // --- Touchpoints Methods ---
    formatTouchpointDate(dateStr) {
      if (!dateStr) return "";
      const d = dayjs(dateStr);
      const today = dayjs().startOf("day");
      const tomorrow = today.add(1, "day");
      if (d.isSame(today, "day")) {
        return `Today at ${d.format("h:mm A")}`;
      }
      if (d.isSame(tomorrow, "day")) {
        return `Tomorrow at ${d.format("h:mm A")}`;
      }
      return d.format("MMM D, YYYY [at] h:mm A");
    },
    getTouchpointBadgeClass(tp) {
      if (!tp) return "bg-gray-100 text-gray-700";
      if (tp.status === "COMPLETED") return "bg-emerald-100 text-emerald-800";
      if (tp.status === "CANCELLED") {
        return "bg-gray-100 text-gray-500 line-through";
      }
      const d = dayjs(tp.scheduledAt);
      if (d.isBefore(dayjs())) {
        return "bg-amber-100 text-amber-800";
      }
      return "bg-blue-100 text-blue-800";
    },
    openTouchpointsModal(card) {
      this.activeTouchpointsCard = card;
      this.isTouchpointsModalOpen = true;
      this.showCreateTouchpointForm = false;
      this.fetchTouchpointsForCard(card);
    },
    openAddTouchpointModal(card) {
      this.activeTouchpointsCard = card;
      this.isTouchpointsModalOpen = true;
      this.showCreateTouchpointForm = true;
      this.newTouchpointForm = {
        title: "",
        meetingType: "MEETING",
        scheduledAt: "",
        location: "",
        notes: "",
        source: "MANUAL",
      };
      this.fetchTouchpointsForCard(card);
    },
    closeTouchpointsModal() {
      this.isTouchpointsModalOpen = false;
      this.activeTouchpointsCard = null;
    },
    fetchTouchpointsForCard(card) {
      if (!card) return;
      this.isLoadingTouchpoints = true;
      this.$api.axios
        .get(`/organization/${card.hashedId}/touchpoints/`)
        .then((resp) => {
          this.touchpointsList = keysToCamel(resp.data);
          this.isLoadingTouchpoints = false;
        })
        .catch(() => {
          this.isLoadingTouchpoints = false;
        });
    },
    submitCreateTouchpoint() {
      if (!this.newTouchpointForm.title || !this.activeTouchpointsCard) return;
      this.isCreatingTouchpoint = true;
      const payload = {
        title: this.newTouchpointForm.title,
        meeting_type: this.newTouchpointForm.meetingType,
        scheduled_at: this.newTouchpointForm.scheduledAt
          ? new Date(this.newTouchpointForm.scheduledAt).toISOString()
          : null,
        location: this.newTouchpointForm.location || null,
        notes: this.newTouchpointForm.notes || null,
        source: "MANUAL",
      };
      this.$api.axios
        .post(
          `/organization/${this.activeTouchpointsCard.hashedId}/touchpoints/`,
          payload,
        )
        .then((resp) => {
          const created = keysToCamel(resp.data);
          this.touchpointsList.unshift(created);
          this.activeTouchpointsCard.touchpointsCount =
            (this.activeTouchpointsCard.touchpointsCount || 0) + 1;
          if (
            !this.activeTouchpointsCard.nextTouchpoint ||
            new Date(created.scheduledAt) <
              new Date(this.activeTouchpointsCard.nextTouchpoint.scheduledAt)
          ) {
            this.activeTouchpointsCard.nextTouchpoint = created;
          }
          this.showCreateTouchpointForm = false;
          this.newTouchpointForm = {
            title: "",
            meetingType: "MEETING",
            scheduledAt: "",
            location: "",
            notes: "",
            source: "MANUAL",
          };
          this.$toast.success("Touchpoint saved successfully");
        })
        .catch(() => {
          this.$toast.error("Failed to save touchpoint");
        })
        .finally(() => {
          this.isCreatingTouchpoint = false;
        });
    },
    markTouchpointCompleted(tp) {
      this.$api.axios
        .put(`/organization/touchpoints/${tp.hashedId}/`, {
          status: "COMPLETED",
        })
        .then((resp) => {
          const updated = keysToCamel(resp.data);
          tp.status = updated.status;
          tp.completedAt = updated.completedAt;
          this.$toast.success("Touchpoint marked as completed");
          if (
            this.activeTouchpointsCard &&
            this.activeTouchpointsCard.nextTouchpoint &&
            this.activeTouchpointsCard.nextTouchpoint.hashedId === tp.hashedId
          ) {
            const remainingUpcoming = this.touchpointsList.filter(
              (t) =>
                t.status === "SCHEDULED" &&
                (!t.scheduledAt || new Date(t.scheduledAt) >= new Date()),
            );
            this.activeTouchpointsCard.nextTouchpoint = remainingUpcoming.length
              ? remainingUpcoming[0]
              : null;
          }
        })
        .catch(() => {
          this.$toast.error("Failed to update touchpoint");
        });
    },
    deleteTouchpoint(tp) {
      this.$api.axios
        .delete(`/organization/touchpoints/${tp.hashedId}/`)
        .then(() => {
          this.touchpointsList = this.touchpointsList.filter(
            (t) => t.hashedId !== tp.hashedId,
          );
          if (
            this.activeTouchpointsCard &&
            this.activeTouchpointsCard.touchpointsCount > 0
          ) {
            this.activeTouchpointsCard.touchpointsCount -= 1;
          }
          if (
            this.activeTouchpointsCard &&
            this.activeTouchpointsCard.nextTouchpoint &&
            this.activeTouchpointsCard.nextTouchpoint.hashedId === tp.hashedId
          ) {
            const remainingUpcoming = this.touchpointsList.filter(
              (t) =>
                t.status === "SCHEDULED" &&
                (!t.scheduledAt || new Date(t.scheduledAt) >= new Date()),
            );
            this.activeTouchpointsCard.nextTouchpoint = remainingUpcoming.length
              ? remainingUpcoming[0]
              : null;
          }
          this.$toast.success("Touchpoint deleted");
        })
        .catch(() => {
          this.$toast.error("Failed to delete touchpoint");
        });
    },

    // --- API Keys & Claude Integration Methods ---
    openApiKeysModal() {
      this.isApiKeysModalOpen = true;
      this.newlyCreatedToken = null;
      this.fetchApiKeys();
    },
    closeApiKeysModal() {
      this.isApiKeysModalOpen = false;
    },
    fetchApiKeys() {
      this.isLoadingApiKeys = true;
      this.$api.axios
        .get("/person/mine/api-key/")
        .then((resp) => {
          this.apiKeysList = keysToCamel(resp.data);
          this.isLoadingApiKeys = false;
        })
        .catch(() => {
          this.isLoadingApiKeys = false;
        });
    },
    generateApiKey() {
      if (!this.newApiKeyName.trim()) return;
      this.isCreatingApiKey = true;
      this.$api.axios
        .post("/person/mine/api-key/", {
          name: this.newApiKeyName.trim(),
        })
        .then((resp) => {
          const created = keysToCamel(resp.data);
          this.newlyCreatedToken = created.token;
          this.apiKeysList.unshift(created);
          this.newApiKeyName = "";
          this.$toast.success("Admin API key created successfully");
        })
        .catch(() => {
          this.$toast.error("Failed to generate API key");
        })
        .finally(() => {
          this.isCreatingApiKey = false;
        });
    },
    deleteApiKey(key) {
      this.$api.axios
        .delete(`/person/mine/api-key/${key.hashedId}/`)
        .then(() => {
          this.apiKeysList = this.apiKeysList.filter(
            (k) => k.hashedId !== key.hashedId,
          );
          this.$toast.success("API key revoked");
        })
        .catch(() => {
          this.$toast.error("Failed to revoke API key");
        });
    },
    copyText(text, type) {
      if (!text) return;
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text);
      }
      if (type === "token") {
        this.copiedToken = true;
        setTimeout(() => {
          this.copiedToken = false;
        }, 2000);
      }
      this.$toast.success("Copied to clipboard");
    },
    copyCurlSnippet() {
      const token = this.newlyCreatedToken || "ns_YOUR_API_KEY";
      const snippet = `curl -X POST "https://api.notedsource.com/api/v0/organization/<ORG_ID>/touchpoints/" \\
  -H "X-API-KEY: ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Quarterly Business Review",
    "meeting_type": "MEETING",
    "scheduled_at": "2026-10-15T14:00:00Z",
    "location": "https://meet.google.com/abc-defg",
    "notes": "Discuss renewal & team expansion",
    "source": "CLAUDE",
    "external_id": "google-calendar-event-id-123"
  }'`;
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(snippet);
      }
      this.copiedCurl = true;
      setTimeout(() => {
        this.copiedCurl = false;
      }, 2000);
      this.$toast.success("Curl snippet copied to clipboard");
    },
  },
};
</script>
